import type { ProethicaTestKind } from "@constants/demo/proethica-tests";
import type {
	Confidence,
	Hint,
	Mode,
	ReportSessionType,
} from "@memorang/types";
import type {
	BaseItemSchema,
	SessionItem,
	SessionResponse,
} from "@memorang/types/src/session/new-session";

interface Answer {
	correct?: boolean;
	confidence?: Confidence;
	timeTaken?: number;
	selectedChoiceIds?: string[];
}

interface ItemState {
	selectedChoiceIds?: string[];
	currentOrders?: string[];
	crossedOutChoiceIds?: string[];
	correctAnswerIds?: string[];
	scores: number[];
	answered?: boolean;
	hints?: Hint[];
	previewChoiceId?: string;
}

type ItemStates = Map<number, ItemState>;
type Answers = Map<number, Answer>;

type UpdateGroupItemStatesType = (
	groupItemId: string,
	actionId: string,
) => void;

export type UpdateItemStatesPayload = {
	itemId: number;
	selectedChoiceId: string;
	multiSelect?: boolean;
	correctAnswerIds?: string[];
	crossOut?: boolean;
	scores?: number[];
	answered?: boolean;
	currentOrders?: string[];
	singleReplace?: boolean;
};
type UpdateItemStatesType = ({
	itemId,
	selectedChoiceId,
	multiSelect,
	correctAnswerIds,
	crossOut,
	scores,
	answered,
	currentOrders,
}: UpdateItemStatesPayload) => void;

export type BlockState = {
	currentProgress?: number;
	completed?: boolean;
	completedStatuses?: {
		[itemId: number]: "completed" | "skipped";
	};
	markedItemsForReview?: number[];
	highlightedItems?: Map<number, string | undefined>;
};

interface SessionState {
	id: number;
	sessionItems: BaseItemSchema[];
	completedTasks?: Record<number, boolean>;
	serverExamBreak?: string;
	showBreakDialog?: boolean;
	breaks?: {
		startTime: string;
		allotedTime: number;
		status: "STARTED" | "EXPIRED";
		totalBreakTimeTakenInSeconds?: number;
	}[];
	examName?: string;
	totalTime: number;
	sessionTime: number;
	breakTimeElapsed: number;
	isBreakOngoing?: boolean;
	currentItemIndex: number;
	startTime?: string;
	showMoreItemsAlert?: boolean;
	timeElapsed: number;
	currentProgress: number;
	isReverseMode?: boolean;
	currentBlockIndex: number;
	reportSessionType?: ReportSessionType;
	mode: Mode;
	numericId: number;
	collectionId?: number;
	answers?: Answers;
	numGoal?: number;
	itemStates: ItemStates;
	numItemsRequested: number;
	isRecommendedTask?: boolean;
	isAnswerEventInProgress?: boolean;
	isSummativeTest?: boolean;
	persistedCurrentProgress?: number;
	persistedSessionItems?: SessionItem[];
	inReviewMode?: boolean;
	blockStates: Map<number, BlockState>;
	isEndingSessionInProgress?: boolean;
	isDiagnostic?: boolean;
	proethicaTestKind?: ProethicaTestKind;
	updateBreak: (breakData?: {
		startTime: string;
		allotedTime: number;
		status: "STARTED" | "EXPIRED";
		totalBreakTimeTakenInSeconds?: number;
	}) => void;

	toggleShowBreakDialog: (show?: boolean) => void;
	toggleShowMoreItemsAlert: () => void;
	updateMarkedItemsForReview: (itemId: number) => void;
	updateHighlightedItems: (itemId: number, html?: string) => void;
	updateCurrentBlockIndex: (index: number) => void;
	updateInReview: (inReview: boolean, items: SessionItem[]) => void;
	gradeItemsForReview: (sessionItems: SessionItem[]) => void;
	updateAnswerEventInProgress: (isAnswerEventInProgress: boolean) => void;
	updateEndingSessionInProgress: (isEndingSessionInProgress: boolean) => void;
	updateCurrentItemIndex: (index: number) => void;
	resetSessionStore: () => void;
	updateAnswers: (id: number, answer: Answer) => void;
	updateItemStates: UpdateItemStatesType;
	setPreviewChoiceId: (itemId: number, choiceId: string) => void;
	updateHintsForItem: (itemId: number, hints: Hint[]) => void;
	updateProgress: (progress: number) => void;
	updateBreakTimeElapsed: (breakTimeElapsed: number) => void;
	updateTimeElapsed: (timeElapsed: number) => void;
	updateProgressForEachBlock: (progress: number, blockId: number) => void;
	toggleBreakOngoing: (isBreakOngoing: boolean) => void;
	updateCompletedTasks: (itemId: number, completed: boolean) => void;
	updateSession: ({
		sessionResponse,
		mode,
		isReverseMode,
		isSummativeTest,
		inReviewMode,
		isDiagnostic,
		proethicaTestKind,
	}: {
		sessionResponse: SessionResponse;
		mode?: Mode;
		isReverseMode?: boolean;
		isSummativeTest?: boolean;
		inReviewMode?: boolean;
		numItemsRequested?: number;
		isDiagnostic?: boolean;
		proethicaTestKind?: ProethicaTestKind;
	}) => void;
}
export type {
	Answer,
	ItemState,
	ItemStates,
	Answers,
	UpdateGroupItemStatesType,
	SessionState,
};
